import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { unifiedProgressService } from '@/services/unifiedProgressService';
import { buildRuntimeExamPath, buildRuntimeHomePath, buildRuntimeReviewPath } from '@/features/unified-learning/runtime/runtimeRoutes';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import type { KpActionRuntime, KpScenarioRuntime, KpSubmitScenarioActionResponse } from '@/types/kursi-play-v2';

const BG = '#0F0F1A';
const SURFACE = '#1E1B3A';
const BORDER = '#2D2B50';
const TEXT = '#FFFFFF';
const TEXT_MED = '#A0A0B8';
const PRIMARY = theme.colors.primary;

type QueueItem = {
  lessonId: string;
  lessonTitle: string;
  scenario: KpScenarioRuntime;
  lessonScenarioCount: number;
};

const MODE_LABELS: Record<string, string> = {
  practice: 'תרגול ממוקד',
  weakness_focus: 'תרגול חולשות',
  topic_focus: 'תרגול לפי נושא',
  mistake_recovery: 'תיקון טעויות',
  mistake_detail_practice: 'תרגול אחרי טעות',
};

export function RuntimePracticeSessionScreen() {
  const { slug, uid, token, lesson_ids, mode, target } = useLocalSearchParams<{
    slug: string;
    uid: string;
    token?: string;
    lesson_ids: string;
    mode?: string;
    target?: string;
  }>();

  const lessonIds = useMemo(() => String(lesson_ids ?? '').split(',').map((x) => x.trim()).filter(Boolean), [lesson_ids]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<KpSubmitScenarioActionResponse | null>(null);
  const [sessionXp, setSessionXp] = useState(0);
  const [completedLessons, setCompletedLessons] = useState<Record<string, boolean>>({});
  const [isSessionComplete, setIsSessionComplete] = useState(false);

  const targetInteractions = Number(target ?? 0) || 0;
  const modeLabel = MODE_LABELS[String(mode ?? 'practice')] ?? 'תרגול';

  useEffect(() => {
    if (!slug || !uid) return;
    void trackKursiPlayEvent('learn_practice_started', { slug, uid, mode: mode ?? 'practice_session' });
    void trackKursiPlayEvent('learn_first_practice_started', { slug, uid, mode: mode ?? 'practice_session' });
  }, [slug, uid, mode]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!slug || !uid || lessonIds.length === 0) {
        setError('חסרים נתונים לסשן תרגול');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const runtimes = await Promise.all(
          lessonIds.map((lessonId) => kpLiveApi.getLessonRuntime(lessonId, String(slug), String(uid))),
        );

        const loadedQueue: QueueItem[] = [];
        for (const rt of runtimes) {
          for (const scenario of rt.scenarios) {
            loadedQueue.push({
              lessonId: rt.lesson.id,
              lessonTitle: rt.lesson.title,
              lessonScenarioCount: rt.scenarios.length,
              scenario,
            });
          }
        }

        const finalQueue = targetInteractions > 0 ? loadedQueue.slice(0, targetInteractions) : loadedQueue;
        if (!cancelled) {
          setQueue(finalQueue);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'שגיאה בטעינת סשן תרגול');
          setLoading(false);
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [slug, uid, lessonIds, targetInteractions]);

  const current = queue[idx];
  const progress = queue.length > 0 ? Math.round(((idx + (result ? 1 : 0)) / queue.length) * 100) : 0;

  const maybeCompleteLesson = useCallback(async (lessonId: string) => {
    if (completedLessons[lessonId]) return;
    const inQueue = queue.filter((q) => q.lessonId === lessonId).length;
    const totalLesson = queue.find((q) => q.lessonId === lessonId)?.lessonScenarioCount ?? 0;
    if (inQueue < totalLesson) return;
    await unifiedProgressService.completeLesson({
      lessonId,
      slug: String(slug),
      userId: String(uid),
      guestSessionToken: token ? String(token) : undefined,
    });
    setCompletedLessons((prev) => ({ ...prev, [lessonId]: true }));
  }, [completedLessons, queue, slug, uid, token]);

  const chooseAction = async (action: KpActionRuntime) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      const response = await kpLiveApi.submitScenarioAction({
        scenario_id: current.scenario.id,
        action_id: action.id,
        lesson_id: current.lessonId,
        slug: String(slug),
        user_id: String(uid),
      });
      setResult(response);
      setSessionXp((x) => x + (response.xp_awarded ?? 0));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בשליחת תשובה');
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (!current) return;
    await maybeCompleteLesson(current.lessonId);
    setResult(null);
    if (idx + 1 >= queue.length) {
      setIsSessionComplete(true);
      void trackKursiPlayEvent('learn_practice_completed', { slug, uid, mode: mode ?? 'practice_session', xp: sessionXp });
      void trackKursiPlayEvent('learn_first_practice_completed', { slug, uid, mode: mode ?? 'practice_session', xp: sessionXp });
      void trackKursiPlayEvent('learn_readiness_checkpoint', { slug, uid, checkpoint: 'practice_completed' });
      return;
    }
    setIdx((i) => i + 1);
  };

  if (loading) return <View style={s.center}><ActivityIndicator color={PRIMARY} size="large" /></View>;
  if (error) {
    return (
      <View style={s.center}>
        <Text style={s.error}>{error}</Text>
        <Pressable style={s.nextBtn} onPress={() => router.replace(buildRuntimeHomePath('/learn/path', String(slug), { uid: String(uid), token: token ? String(token) : undefined }) as any)}>
          <Text style={s.nextText}>חזרה לבית המסלול</Text>
        </Pressable>
      </View>
    );
  }
  if (isSessionComplete) {
    return (
      <View style={s.center}>
        <View style={s.resultCard}>
          <Text style={s.resultTitle}>סשן התרגול הושלם</Text>
          <Text style={s.body}>צברת {sessionXp} XP. עכשיו כדאי לסגור את הלולאה עם סקירת טעויות או בדיקת מוכנות.</Text>
          <Pressable style={s.nextBtn} onPress={() => router.replace(buildRuntimeReviewPath('/learn/path', String(slug), { uid: String(uid), token: token ? String(token) : undefined }) as any)}>
            <Text style={s.nextText}>מעבר לסקירת טעויות</Text>
          </Pressable>
          <Pressable style={s.actionBtn} onPress={() => router.replace(buildRuntimeExamPath('/learn/path', String(slug), { uid: String(uid), token: token ? String(token) : undefined }) as any)}>
            <Text style={s.actionText}>בדיקת מוכנות קצרה</Text>
          </Pressable>
          <Pressable style={s.actionBtn} onPress={() => router.replace(buildRuntimeHomePath('/learn/path', String(slug), { uid: String(uid), token: token ? String(token) : undefined }) as any)}>
            <Text style={s.actionText}>חזרה לבית המסלול</Text>
          </Pressable>
        </View>
      </View>
    );
  }
  if (!current) {
    return (
      <View style={s.center}>
        <Text style={s.error}>לא נמצאו תרחישים לתרגול.</Text>
        <Pressable style={s.nextBtn} onPress={() => router.replace(buildRuntimeHomePath('/learn/path', String(slug), { uid: String(uid), token: token ? String(token) : undefined }) as any)}>
          <Text style={s.nextText}>חזרה לבית המסלול</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={s.root}>
      <View style={s.top}><Text style={s.topTitle}>סשן תרגול · {modeLabel}</Text><Text style={s.topSub}>XP בסשן: {sessionXp}</Text></View>
      <View style={s.progressTrack}><View style={[s.progressFill, { width: `${progress}%` as any }]} /></View>

      <ScrollView contentContainerStyle={s.content}>
        <Text style={s.lessonLabel}>{current.lessonTitle}</Text>
        <Text style={s.scenarioTitle}>{current.scenario.title}</Text>
        <Text style={s.body}>{current.scenario.situation}</Text>
        <Text style={s.body}>{current.scenario.decision_point}</Text>

        {!result && current.scenario.actions.map((action) => (
          <Pressable key={action.id} style={s.actionBtn} onPress={() => chooseAction(action)} disabled={submitting}>
            <Text style={s.actionText}>{action.label}</Text>
          </Pressable>
        ))}

        {result && result.consequence && (
          <View style={s.resultCard}>
            <Text style={s.resultTitle}>{result.consequence.outcome_label} · +{result.xp_awarded} XP</Text>
            <Text style={s.body}>{result.consequence.narrative}</Text>
            {result.reinforcements.slice(0, 2).map((r, ri) => (
              <View key={`${ri}-${r.headline}`} style={s.reinfRow}>
                <Text style={s.reinfTitle}>{r.headline}</Text>
                <Text style={s.reinfBody}>{r.body}</Text>
              </View>
            ))}
            <Pressable style={s.nextBtn} onPress={() => { void goNext(); }}>
              <Text style={s.nextText}>{idx + 1 >= queue.length ? 'סיום סשן' : 'לתרחיש הבא'}</Text>
            </Pressable>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  top: { paddingTop: 50, paddingHorizontal: 18, paddingBottom: 8 },
  topTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16 },
  topSub: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, marginTop: 2 },
  progressTrack: { height: 5, backgroundColor: '#2A274A' },
  progressFill: { height: 5, backgroundColor: PRIMARY },
  content: { padding: 18, gap: 10, paddingBottom: 50 },
  lessonLabel: { color: PRIMARY, fontFamily: theme.fonts.bold, textAlign: 'right' },
  scenarioTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 22, textAlign: 'right' },
  body: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22, textAlign: 'right' },
  actionBtn: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 12 },
  actionText: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  resultCard: { marginTop: 8, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  resultTitle: { color: '#34D399', fontFamily: theme.fonts.bold, textAlign: 'right' },
  reinfRow: { backgroundColor: '#16142A', borderRadius: 10, padding: 10 },
  reinfTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 13, textAlign: 'right' },
  reinfBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, textAlign: 'right', marginTop: 4 },
  nextBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 11 },
  nextText: { color: '#fff', fontFamily: theme.fonts.bold },
  error: { color: '#fff', fontFamily: theme.fonts.bold },
});
